import React, { useState, useRef, useEffect } from 'react';
import jsCookie from 'js-cookie';
import { useSearchParams } from 'react-router-dom';
import SentIcon from 'src/components/icons/common/send';
import { useAppDispatch } from 'src/hooks/redux';
import { mapColumnsChat } from './action';

interface Props {
    file: File;
}

interface Message {
    id: number;
    sender: 'user' | 'system';
    content: string;
    encodedPlot?: string; // ✅ support optional plot
}

const AutoMappedChat: React.FC<Props> = ({ file }) => {
    const dispatch = useAppDispatch();
    const userId = jsCookie.get('user_id');
    const [searchParams] = useSearchParams();
    const threadId = searchParams.get('threadId');

    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [streamedContent, setStreamedContent] = useState('');
    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setMessages([]);
    }, [file]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, streamedContent]);

    const sendMessage = async () => {
        if (!input.trim()) return;

        const userMessage: Message = {
            id: Date.now(),
            sender: 'user',
            content: input.trim(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInput('');
        setLoading(true);
        setStreamedContent('');

        try {
            const response = await dispatch(
                mapColumnsChat({
                    userId: userId || '',
                    threadId: threadId || '',
                    file,
                    query: input.trim(),
                    include_plot: true,
                })
            ).unwrap();

            const fullText = response.data.natural_language_response;
            const encodedPlot = response.data.encoded_plot || undefined;

            let current = '';
            for (let i = 0; i < fullText.length; i++) {
                current += fullText[i];
                setStreamedContent(current);
                await new Promise((res) => setTimeout(res, 10));
            }

            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now(),
                    sender: 'system',
                    content: fullText,
                    encodedPlot, // ✅ attach plot if present
                },
            ]);
        } catch (err) {
            setMessages((prev) => [
                ...prev,
                {
                    id: Date.now(),
                    sender: 'system',
                    content: 'Something went wrong. Please try again.',
                },
            ]);
        } finally {
            setLoading(false);
            setStreamedContent('');
        }
    };

    return (
        <div className="max-w-3xl h-[45vh] border rounded-lg flex flex-col overflow-hidden shadow-md">
            {/* Chat area */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-gray-50">
                {messages.map((msg) => (
                    <div key={msg.id} className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}>
                        <div
                            className={`px-3 py-2 rounded-xl ${msg.sender === 'system' ? 'max-w-[90%]' : 'max-w-[75%]'} text-sm shadow ${msg.sender === 'system'
                                ? 'border border-gray-200 bg-white text-gray-900 rounded-br-none'
                                : 'bg-foundationOrange-100 text-gray-900 rounded-bl-none'
                                }`}
                        >
                            {msg.content}
                        </div>

                        {/* ✅ Show plot below system message if available */}
                        {msg.sender === 'system' && msg.encodedPlot && (
                            <img
                                src={`data:image/png;base64,${msg.encodedPlot}`}
                                alt="Plot"
                                className="mt-2 rounded-lg shadow-md w-[90%] border border-gray-200"
                            />
                        )}
                    </div>
                ))}

                {loading && (
                    <div className="flex justify-start">
                        <div className="px-2 py-1 rounded-xl max-w-[90%] text-xs border border-gray-200 bg-white shadow-md rounded-br-none animate-pulse">
                            {streamedContent || 'Searching...'}
                        </div>
                    </div>
                )}
                <div ref={chatEndRef} />
            </div>

            {/* Input area */}
            <div className="border-t bg-white flex items-center space-x-1">
                <input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    className="flex-1 p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none overflow-y-auto"
                    placeholder="Type here"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            sendMessage();
                        }
                    }}
                />
                <button
                    onClick={sendMessage}
                    className="bg-primary-900 text-white rounded-full h-4 w-4 flex items-center justify-center cursor-pointer"
                    type="button"
                >
                    <SentIcon className="h-2 w-2" />
                </button>
            </div>
        </div>
    );
};

export default AutoMappedChat;